import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles1() {
  const { lesson, course, accent } = findLesson("the-beatles", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The key question" title="A minor — but which kind?">
        <Prose>
          <p>
            The first thing you notice about "Now and Then" is that the F chord sounds slightly
            wrong. Not wrong enough to be a mistake — wrong enough to make you ask: is this song
            in A natural minor, or is it A Dorian? That question doesn't have a clean answer.
            Jason's position is deliberate: <em>"We are going to call it A hybrid minor for now,
            because there's no actual definition or defined minor."</em>
          </p>
          <p>
            A natural minor has F natural as its sixth degree. A Dorian has F sharp. In the song,
            both appear to work. And a G sharp surfaces in the bass at one point — which points toward
            the harmonic minor. Because the song floats freely between all three minor flavours, the
            honest position is to map out every available option and let the melody and bass confirm
            the chord at each moment.
          </p>
          <p>
            This is not just a "Now and Then" situation. Many great songs live in this ambiguity.
            Calling it A minor, A Dorian, or A harmonic minor each gives you a different set of
            legal chords. Calling it A hybrid minor gives you all of them — and lets the music itself
            decide which feels right bar by bar.
          </p>
        </Prose>

        <TheoryBox label="A hybrid minor — all available chords at the sixth degree">
          Starting from A natural minor (A B C D E F G), the sixth degree chord can take three
          different forms depending on which scale is active: ·
          <strong>F major</strong> — A natural minor (F A C). The "default" option. ·
          <strong>F♯ diminished</strong> — A Dorian (F♯ A C). More colour. ·
          <strong>F minor</strong> — F A♭ C. Jason links it to A harmonic minor: A♭ is the same
          key as that scale's G♯, so all three keys of the chord come from it. Highly rare, but
          he tries it against the verse melody and it works. ·
          Jason's test: "F major works. F♯ diminished works. All those three chords work, you know."
          His method: "I like to not commit to anything. I like to see what are all the available
          options."
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="Opxhh9Oh3rg"
        title="Now and Then — The Beatles (2023)"
        caption="Listen to the verse. The F chord arrives in line three — notice how it opens things up. Then at the word 'you,' the chord briefly suspends with no third. That suspension is the Asus2sus4."
      />

      <Chapter eyebrow="The verse melody" title="Vocals off the beat — until line four">
        <Prose>
          <p>
            The verse melody of "Now and Then" is rhythmically subtle. In 4/4, with a straight
            feel (not swing), the vocal never lands on beat one for the first three lines. Each
            phrase enters <em>after</em> beat one — a rest precedes every entry — and the notes
            hold across bar lines with long tied values. Only in line four do the vocals land
            directly on the downbeat.
          </p>
          <p>
            Jason's notation method for this kind of off-beat phrasing relies on ties rather than
            rests wherever a note continues across a bar line: "A tie doesn't mean a new note. It
            means the note is getting continued. See, the sound or the sustain of the note is
            getting continued. If you're getting confused with the tie, you can avoid writing it and
            put a rest. But in this case, because it's a vocal line, it's recommended to put the tie."
          </p>
          <p>
            Lines one and three have consistent endings. Lines two and four each change in their
            final bar — the second verse has slightly different words and therefore slightly different
            melodic endings from the first. The first verse's fourth line ends one way; the second
            verse's fourth line moves differently as it sets up the chorus.
          </p>
        </Prose>

        <MTQuote>
          "You can observe exactly what's going on at the fourth line only. The vocals hit on the
          beat, while at the first, second and third line, the vocals do not hit on the beat —
          they are hitting after beat one, being represented by this rest here, and getting it:
          one, two, and three, four. And there are a lot of these gaps."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The chords" title="Em/G and the sus2 sus4 suspension">
        <Prose>
          <p>
            Line one of the verse establishes the harmonic identity of the song. The opening chord
            is A minor — the home. The chord that arrives almost immediately is far more interesting:
            <strong> E minor over G</strong> (Em/G), a slash chord with G in the bass instead of E.
            Jason calls it "a beautiful E minor over G chord" — "It's making E minor a lot more
            sadder." With G in the bass, the minor chord turns melancholic.
          </p>
          <p>
            Line three is where the song breathes: A minor moves to F major, and F major gives the
            line exactly the harmonic openness it needs. Then
            line four lands on E7 — the authentic cadence (5 going to 1) — which pulls everything
            back to A minor with conviction and loops the verse forward.
          </p>
          <p>
            The most unusual moment arrives at the word <em>you</em>. Here the piano suspends not
            just one note but two: the second (B) and the fourth (D), with the third absent entirely.
            This is <strong>A sus2 sus4</strong> — the notes A, B, D. No C. No third. If you
            <em>add</em> a note to a chord, the third stays; if you <em>suspend</em>, it goes. As
            Jason puts it: "If we suspend there is not going to be the third. It's just going to be
            some other note."
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Verse — A hybrid minor chord progression"
          caption="Lines 1–2: Am → Em/G. Line 3: Am → F. Line 4: Am → F → E7 → Asus2sus4. The Em/G slash chord and the Asus2sus4 (A B D, no third) are the two harmonic signatures of the verse."
          chords={[
            { chord: "Am", rn: "i", function: "Tonic" },
            { chord: "Em/G", rn: "v⁶", function: "Dominant" },
            { chord: "Am", rn: "i", function: "Tonic" },
            { chord: "F", rn: "♭VI", function: "Pre-dominant" },
            { chord: "E7", rn: "V7", function: "Dominant" },
            { chord: "Asus2sus4", rn: "i(sus)", function: "Tension" },
            { chord: "Am", rn: "i", function: "Tonic" },
          ]}
        />

        <TheoryBox label="Authentic cadence and plagal cadence">
          Jason introduces both cadence types while working out the verse chords.
          The <strong>authentic cadence</strong> (also called the perfect cadence) is 5 going
          to 1 — in A minor, that's E7 resolving to Am. The tension of the dominant seventh
          resolves cleanly home. The <strong>plagal cadence</strong> is 4 going to 1 — in A
          minor, that's Dm (or D major, borrowed from A Dorian) going to Am. It sounds more open and peaceful than the authentic
          cadence. "We call this as the authentic cadence, which is also known as a five to one.
          And we call this a plagal cadence, which is also known as a four to one." The E7 in the
          verse fourth line is a textbook authentic cadence.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The chorus" title="Now and then — the Dorian lift">
        <Prose>
          <p>
            When the chorus arrives, the harmonic colour shifts.
            "There, they've brought in the Dorian," Jason observes while listening. The scale
            has moved — or more precisely, the chord choices now draw from A Dorian, which shares
            exactly the same notes as G major. "You could say it's A Dorian. The other is you
            could say it moved to G major."
          </p>
          <p>
            In A Dorian / G major, the chord on D is D major — in A natural minor it would be
            D minor. Jason's chorus chords lean on D major, with D minor appearing too: the "four
            major to four minor" move he mentions at the start of the class. Right after the title
            words, the melody goes A, A, D, D.
          </p>
          <p>
            The guitar lick that precedes the chorus is also worth learning separately. Jason
            notes: "These Beatles licks should be learned. You shouldn't skip the licks." And
            the drum roll before the chorus arrival is notable — the drummer physically transporting
            you to the new section with a fill.
          </p>
        </Prose>

        <MTQuote>
          "The notes are absolutely the same, isn't it? See, if a relative minor is at the sixth
          degree, a Dorian is at the second degree."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Playing it" title="Two voicing approaches for solo piano">
        <Prose>
          <p>
            When playing "Now and Then" solo at the piano, Jason presents two left-hand approaches.
            Option one is block chords — hit all the notes of the chord simultaneously, four hits
            per bar. Option two separates the bass note from the chord: play the bass note low,
            then jump up to play the chord body. "Option 2 would be you're playing the bass note of
            the chord down below" — giving you the bass-and-chord sound without a band.
          </p>
          <p>
            The Em/G chord is the most interesting to voice in option two. G goes in the bass (low
            left hand), and the E minor triad sits above it in the mid-register. The G note appears
            in both positions, which strengthens the bass character of the chord. A student tries a
            stride version — bass note, then a jump to the chord — and Jason's tip is the sustain
            pedal: keep it down through the jump, and lift it before each new chord.
          </p>
          <p>
            For the A sus2 sus4 (A, B, D), hold the notes without resolving. The absence of a third
            makes it float — neither fully major nor fully minor — and that is precisely the emotional
            quality the Beatles were after at the word "you." The chord reflects the suspension of
            the moment in the lyric.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/01-now-and-then.png"
          caption="Now and Then — verse melody notation and chord map. Note the off-beat vocal entrances (lines 1–3) against the on-beat entry of line 4. Em/G and Asus2sus4 are both marked at their exact bar positions."
        />
      </Chapter>

      <Chapter eyebrow="Learning by ear" title="Moises — stem separation in practice">
        <Prose>
          <p>
            Jason uses the Moises app throughout this class as a teaching tool, not a shortcut.
            Removing the vocals lets you hear exactly what the piano is doing. Isolating the bass
            confirms the root of each chord. "You can even mute the vocals, by the way. You see you
            can hear exactly what the piano is doing now. Why can you hear what the piano is doing?
            Because there's no vocals. So what's left is the piano. And the vocals are generally
            pretty loud in songs, don't you think? So it's a good app, Moises."
          </p>
          <p>
            The practical workflow: buy the song on iTunes (streaming services don't allow downloads
            for third-party stem separation). Import it into Moises, which gives you five separate
            tracks — vocals, drums, bass, piano, other. The waveform view also shows detected
            sections, so you can see exactly where the verse ends and the chorus begins. Jason
            recommends the musician subscription (about 30 dollars a year) for unlimited imports;
            the basic plan is free.
          </p>
          <p>
            One important caution: "Don't look at the chords in the bottom. I don't know how you
            can avoid staring at the chords. There must be a way." Jason would like Moises to give
            students a way to switch the chord display off. Until then, use the
            stem separation and the waveform. Figure out the chords yourself. The app shows them
            automatically, but reading them defeats the ear-training purpose.
          </p>
        </Prose>
      </Chapter>
    </LessonShell>
  );
}
